import React, { useState, useEffect, useRef } from "react";
import "../../../assets/scss/views/SettingsVehicles.scss";

// Componentes de tablas existentes
import { VehicleBrandTable } from "../../../components/ui/tables/VehicleBrandTable";
import { OperatorTable } from "../../../components/ui/tables/OperatorTable";
import { OwnerTable } from "../../../components/ui/tables/OwnerTable";
import { VehicleTypeTable } from "../../../components/ui/tables/VehicleTypeTable";
import { OperationsTable } from "../../../components/ui/tables/OperationsTable";
import { DocumentRequirementsManager } from "../../../components/ui/tables/DocumentRequirementsManager";
import { DocumentVehicleTypesTable } from "../../../components/ui/tables/DocumentVehicleTypesTable";
import { useLayout } from "../../../context/LayoutContext";
import { CourseTable } from "../../../components/ui/tables/CourseTable";
import { CourseRequirementsManager } from "../../../components/ui/tables/CourseRequirementsManager";
import { CriticalPointsTableAdmi } from "../../../components/ui/tables/CriticalPointsTableAdmi";
import { PredefinedRoutesTable } from "../../../components/ui/tables/PredefinedRoutesTable";
import { SatelliteProviderSettings } from "../../../components/ui/tables/SatelliteProviderSettings.jsx";
import { LocationReportSettings } from "../../../components/ui/tables/LocationReportSettings.jsx";

export function SettingVehicle() {
  const { theme } = useLayout();
  const [activeTab, setActiveTab] = useState("vehicleTypes");
  const [currentPage, setCurrentPage] = useState(0);
  const indicatorRef = useRef(null);
  const tabContainerRef = useRef(null);
  const tabsWrapperRef = useRef(null);

  // Definición de todas las pestañas
  const allTabs = [
    { id: "vehicleTypes", label: "Tipos de Vehículo" },
    { id: "brands", label: "Marcas" },
    { id: "owners", label: "Propietarios" },
    { id: "operators", label: "Operadores" },
    { id: "operations", label: "Operaciones" },
    { id: "documentTypes", label: "Tipos de Documentos" },
    { id: "requirements", label: "Requisitos Documentales" },
    { id: "courses", label: "Cursos" },
    { id: "courseRequirements", label: "Requisitos de Cursos" },
    { id: "criticalPoints", label: "Puntos Críticos" },
    { id: "predefinedRoutes", label: "Rutas Predefinidas" },
    { id: "satelliteProviders", label: "Proveedores Satelitales" },
    { id: "locationReports", label: "Reporte Ubicación" }
  ];

  const TABS_PER_PAGE = 8;
  const totalPages = Math.ceil(allTabs.length / TABS_PER_PAGE);
  
  // Obtener las pestañas visibles para la página actual
  const getVisibleTabs = () => {
    const start = currentPage * TABS_PER_PAGE;
    const end = start + TABS_PER_PAGE;
    return allTabs.slice(start, end);
  };

  // Manejador para cambiar entre pestañas
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  // Navegación entre páginas de pestañas
  const navigateTabs = (direction) => {
    if (direction === 'prev' && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    } else if (direction === 'next' && currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Efecto para asegurar que la pestaña activa esté visible
  useEffect(() => {
    const activeTabIndex = allTabs.findIndex(tab => tab.id === activeTab);
    const activePage = Math.floor(activeTabIndex / TABS_PER_PAGE);
    if (activePage !== currentPage) {
      setCurrentPage(activePage);
    }
  }, [activeTab]);

  // Efecto para manejar la animación del indicador
  useEffect(() => {
    if (indicatorRef.current && tabsWrapperRef.current) {
      const activeButton = tabsWrapperRef.current.querySelector(
        `[data-tab="${activeTab}"]`
      );
      if (activeButton) {
        const buttonRect = activeButton.getBoundingClientRect();
        const containerRect = tabsWrapperRef.current.getBoundingClientRect();
        const left = buttonRect.left - containerRect.left;
        
        indicatorRef.current.style.transform = `translateX(${left}px)`;
        indicatorRef.current.style.width = `${buttonRect.width}px`;
      }
    }
  }, [activeTab, currentPage]);

  return (
    <div className={`GM__${theme}-dashboard-vehicle`}>
      <h1>Configuraciones de Vehículos</h1>

      {/* Contenedor de navegación de pestañas con paginación */}
      <div className={`GM__${theme}-settings-tabs`}>
        <div className={`GM__${theme}-tab-navigation-container`}>
          {/* Botón de navegación anterior */}
          <button
            className={`GM__${theme}-tab-nav-button ${currentPage === 0 ? 'disabled' : ''}`}
            onClick={() => navigateTabs('prev')}
            disabled={currentPage === 0}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Contenedor de pestañas con indicador */}
          <div 
            className={`GM__${theme}-tab-indicator-container`}
            ref={tabContainerRef}
          >
            <div className={`GM__${theme}-tabs-wrapper`} ref={tabsWrapperRef}>
              <div
                className={`GM__${theme}-tab-indicator`}
                ref={indicatorRef}
              ></div>

              {getVisibleTabs().map((tab) => (
                <button
                  key={tab.id}
                  className={`GM__${theme}-tab-button ${
                    activeTab === tab.id ? `GM__${theme}-active` : ""
                  }`}
                  onClick={() => handleTabChange(tab.id)}
                  data-tab={tab.id}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Botón de navegación siguiente */}
          <button
            className={`GM__${theme}-tab-nav-button ${currentPage === totalPages - 1 ? 'disabled' : ''}`}
            onClick={() => navigateTabs('next')}
            disabled={currentPage === totalPages - 1}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Indicador de página actual */}
          <div className={`GM__${theme}-tab-page-indicator`}>
            {currentPage + 1} / {totalPages}
          </div>
        </div>
      </div>

      {/* Contenido de las pestañas */}
      <div className={`GM__${theme}-tab-content`}>
        {activeTab === "vehicleTypes" && <VehicleTypeTable />}
        {activeTab === "brands" && <VehicleBrandTable />}
        {activeTab === "owners" && <OwnerTable />}
        {activeTab === "operators" && <OperatorTable />}
        {activeTab === "operations" && <OperationsTable />}
        {activeTab === "documentTypes" && <DocumentVehicleTypesTable />}
        {activeTab === "requirements" && <DocumentRequirementsManager />}
        {activeTab === "courses" && <CourseTable />}
        {activeTab === "courseRequirements" && <CourseRequirementsManager />}
        {activeTab === "criticalPoints" && <CriticalPointsTableAdmi />}
        {activeTab === "predefinedRoutes" && <PredefinedRoutesTable />}
        {activeTab === "satelliteProviders" && <SatelliteProviderSettings />}
        {activeTab === "locationReports" && <LocationReportSettings />}
      </div>
    </div>
  );
}